import { ApiResponse } from '@/types/apiResponse';
import { apiService } from '../api';

export interface DetectedCountry {
  country: string | null;
  countryCode: string | null;
}

export interface CountryCheckResult {
  countryCode: string | null;
  isSequenceBlocked: boolean;
}

class LocationService {
  private static instance: LocationService;

  private constructor() {}

  public static getInstance(): LocationService {
    if (!LocationService.instance) {
      LocationService.instance = new LocationService();
    }
    return LocationService.instance;
  }

  private getCurrentPosition(): Promise<{
    latitude: number;
    longitude: number;
  }> {
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        pos =>
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          }),
        err => reject(err),
        { enableHighAccuracy: false, timeout: 30000, maximumAge: 0 },
      );
    });
  }

  async detectCountry(): Promise<ApiResponse<DetectedCountry>> {
    const { latitude, longitude } = await this.getCurrentPosition();
    return apiService.post<ApiResponse<DetectedCountry>>(
      '/public/api/v1/location/detect-country',
      { latitude, longitude },
    );
  }

  async countryCheck(): Promise<ApiResponse<CountryCheckResult>> {
    // Dev-only: pass ?mockCountry= from URL to backend for local testing.
    // E.g. open http://localhost:5173?mockCountry=IR to simulate a blocked country.
    let mockParam = '';
    if (import.meta.env.DEV) {
      const urlParams = new URLSearchParams(window.location.search);
      const mock = urlParams.get('mockCountry');
      if (mock) {
        mockParam = `?mockCountry=${encodeURIComponent(mock)}`;
      }
    }

    return apiService.get<ApiResponse<CountryCheckResult>>(
      `/public/api/v1/location/country-check${mockParam}`,
    );
  }
}

export const locationService = LocationService.getInstance();
